import AccessTimeOutlinedIcon from "@mui/icons-material/AccessTimeOutlined";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import LaunchOutlinedIcon from "@mui/icons-material/LaunchOutlined";
import LocationOnIcon from "@mui/icons-material/LocationOn";
// import PeopleIcon from "@mui/icons-material/People";
// import StarIcon from "@mui/icons-material/Star";
import { Dialog } from "@mui/material";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Divider from "@mui/material/Divider";
import Typography from "@mui/material/Typography";
import dayjs from "dayjs";
import { get } from "radash";
import { FC, useState } from "react";
import { User } from "src/services/dto/Security";

import { getFullNameAndInitials } from "@utils/helpers";

type ProviderProfileProps = {
  provider: User;
  openBio?: boolean;
  bioOnly?: boolean;
  handleBioClose?: () => void;
};

const ProfileHeader: React.FC<{
  name: string;
  title: string;
  imageUrl: string;
  renderShowBio?: boolean;
  handleViewBio: () => void;
}> = ({ name, title, imageUrl, renderShowBio = true, handleViewBio }) => (
  <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
    <Avatar
      src={imageUrl}
      sx={{
        width: 140,
        height: 140,
        borderRadius: 0,
        mr: 1,
        backgroundColor: "background.light",
      }}
    />
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Typography variant="h6" fontWeight="bold" noWrap>
        {name}
      </Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          justifyContent: "space-between",
          alignItems: "center",
          minWidth: 0,
          marginTop: 0.5,
        }}
      >
        <Typography variant="body2" noWrap sx={{ flexShrink: 1, minWidth: 0 }}>
          {title}
        </Typography>
        {renderShowBio && (
          <Button
            variant="outlined"
            size="small"
            onClick={handleViewBio}
            sx={{ paddingY: 0 }}
          >
            View Bio
          </Button>
        )}
      </Box>
    </Box>
  </Box>
);

const InfoItem: React.FC<{
  title?: string;
  icon: JSX.Element;
  value: string | number;
  showRightBorder?: boolean;
}> = ({ icon, value, title, showRightBorder }) => (
  <Box
    sx={{
      paddingY: 1,
      flex: 1,
      borderRight: showRightBorder ? 1 : "none",
      borderColor: "divider",
      minWidth: 0,
    }}
  >
    <Box
      sx={{
        display: "flex",
        gap: 0.5,
        paddingX: 0.5,
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      {icon}
      <Typography
        variant="body1"
        noWrap
        sx={{
          flex: 1,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          maxWidth: "100%",
        }}
      >
        {value}
      </Typography>
    </Box>
    {title && (
      <Typography variant="body2" paddingX={0.5}>
        {title}
      </Typography>
    )}
  </Box>
);

const getProviderInfo = (provider: ProviderProfileProps["provider"]) => {
  const name = getFullNameAndInitials(provider).fullName;
  const imageUrl = get(provider, "headUrl", "");
  const title = get(provider, "providerInfo.qualifications", []).join(", ");
  const location = get(provider, "state", "").split(",").join(", ");
  const bio = get(provider, "providerInfo.biography");

  return {
    location,
    name,
    title,
    imageUrl,
    bio,
  };
};

const ProviderProfile: FC<ProviderProfileProps> = ({
  provider,
  openBio = false,
  bioOnly = false,
  handleBioClose,
  children,
}) => {
  const [showBio, setShowBio] = useState(openBio);
  const { location, name, title, imageUrl, bio } = getProviderInfo(provider);
  const info = [
    // { title: "Rating", icon: <StarIcon color="primary" />, value: 4.6 },
    // {
    //   title: "Patients",
    //   icon: <PeopleIcon color="primary" />,
    //   value: 2565,
    // },
    {
      title: "Licensed",
      icon: <LocationOnIcon color="primary" />,
      value: location,
    },
  ];

  const profile = (
    <>
      <ProfileHeader
        name={name}
        title={title}
        imageUrl={imageUrl}
        renderShowBio={!showBio}
        handleViewBio={() => setShowBio(true)}
      />
      <Divider />
      <Box sx={{ display: "flex", justifyContent: "space-evenly" }}>
        {info.map((item, i) => {
          const showBorder = i !== info.length - 1;
          return (
            <InfoItem
              key={item.title}
              icon={item.icon}
              value={item.value}
              title={item.title}
              showRightBorder={showBorder}
            />
          );
        })}
      </Box>
    </>
  );

  const bioModal = (
    <Dialog
      open={showBio}
      onClose={() => {
        setShowBio(false);
        handleBioClose?.();
      }}
      fullWidth
    >
      {profile}
      {bio && (
        <Box sx={{ padding: 1 }}>
          <Typography variant="h5" pb={1}>
            Bio
          </Typography>
          <Typography variant="body2">{bio}</Typography>
        </Box>
      )}
    </Dialog>
  );
  if (bioOnly)
    return (
      <Card elevation={0} sx={{ width: "100%" }}>
        {bioModal}
      </Card>
    );
  else
    return (
      <Card elevation={0} sx={{ width: "100%" }}>
        {profile}
        <Divider />
        {bioModal}
        {children}
      </Card>
    );
};

interface EventInfoProps {
  provider: User;
  eventStart: string;
  eventEnd: string;
  tz: string;
}

export const EventInfo: React.FC<EventInfoProps> = ({
  provider,
  eventStart,
  eventEnd,
  tz,
}) => {
  const [showBio, setShowBio] = useState(false);

  const startTime = dayjs(eventStart).tz(tz);
  const endTime = dayjs(eventEnd).tz(tz);

  // Format for the first requirement: "Tues, Feb 03, 2025"
  const formattedDate = startTime.format("ddd, MMM DD, YYYY");

  // Format for the second requirement: "2:00-2:40 PM"
  const formattedTimeRange = `${startTime.format("h:mm")}-${endTime.format("h:mm A")}`;

  const LINK_FORMAT = "YYYYMMDDTHHmmssZ";
  const calendarLink = `https://www.google.com/calendar/render?action=TEMPLATE&text=Ognomy+Sleep+Medicine+Consultation&dates=${dayjs(startTime).format(LINK_FORMAT)}/${dayjs(endTime).format(LINK_FORMAT)}&details=To+join+your+virtual+appointment,+go+to+your+<a href="https://patient.ognomy.com/signInPage">Ognomy+dashboard</a>&location=Virtual&sf=true&output=xml`;

  const info = [
    {
      icon: <CalendarMonthOutlinedIcon sx={{ color: "background.light" }} />,
      value: formattedDate,
    },
    {
      icon: <AccessTimeOutlinedIcon sx={{ color: "background.light" }} />,
      value: formattedTimeRange,
    },
  ];
  const { name, title, imageUrl } = getProviderInfo(provider);

  return (
    <Card elevation={0} sx={{ width: "100%" }}>
      <ProfileHeader
        name={name}
        title={title}
        imageUrl={imageUrl}
        handleViewBio={() => setShowBio(true)}
      />
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-evenly",
          color: "white",
          fontWeight: 700,
          backgroundColor: "primary.main",
        }}
      >
        {info.map((item, i) => {
          const showBorder = i !== info.length - 1;
          return (
            <InfoItem
              key={item.value}
              icon={item.icon}
              value={item.value}
              showRightBorder={showBorder}
            />
          );
        })}
      </Box>
      <Divider />
      <Box
        sx={{
          backgroundColor: "primary.main",
        }}
      >
        <Button
          fullWidth
          onClick={() => window.open(calendarLink, "_blank")}
          sx={{
            paddingY: 1,
            borderRadius: 0,
            color: "white",
            backgroundColor: "primary.main",
          }}
        >
          <LaunchOutlinedIcon
            sx={{ color: "inherit", textAlign: "center", mr: 0.5 }}
          />
          Add to Google Calendar
        </Button>
      </Box>
      {showBio && (
        <ProviderProfile
          bioOnly
          openBio
          provider={provider}
          handleBioClose={() => setShowBio(false)}
        />
      )}
    </Card>
  );
};

export default ProviderProfile;
